import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { LuMail, LuMapPin, LuSend, LuUserPlus, LuUserRound, LuX } from "react-icons/lu";
import { useLocationMaster } from "../../hooks/useLocationMaster";
import {
  createAndInviteLogisticsProvider,
  checkInviteLogisticsProviderEmail,
  extractApiMessage,
} from "../../services/inviteLogisticsProviders.service";
import CommonService from "../../services/common.service";
import "../../assets/styles/super_admin_access.scss";

const SectionHeading = ({ number, title, description, icon: Icon }) => (
  <div className="sa-user-form__section-head">
    <div className="sa-user-form__section-copy">
      <span className="sa-user-form__step">{number}</span>
      <span>
        <strong>{title}</strong>
        <small>{description}</small>
      </span>
    </div>
    <Icon aria-hidden />
  </div>
);

const LOGISTICS_PROVIDER_EMAIL_EXISTS =
  "A Logistics Provider account with this email already exists.";

const EMPTY_FORM = {
  companyName: "",
  contactPersonName: "",
  email: "",
  mobile: "",
  alternateMobile: "",
  status: "Active",
  addressLine1: "",
  addressLine2: "",
  countryId: "",
  stateId: "",
  cityId: "",
  pincode: "",
};

function labelForId(list, id) {
  const match = list.find((item) => String(item.id) === String(id));
  return match?.label || match?.name || "";
}

export default function CreateLogisticsProviderInviteModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [invitedBy, setInvitedBy] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [emailChecking, setEmailChecking] = useState(false);
  const emailCheckSeq = useRef(0);

  const {
    countries,
    states,
    cities,
    loading: loadingCountries,
    loadingStates,
    loadingCities,
    updateListsForCountry,
    updateListsForState,
  } = useLocationMaster();

  const handleCountryChange = (countryId) => {
    void updateListsForCountry(countryId || null);
  };

  const handleStateChange = (countryId, stateId) => {
    void updateListsForState(countryId, stateId || null);
  };

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setErrors({});
    setEmailHint("");
    let cancelled = false;
    (async () => {
      try {
        const res = await CommonService.get("/logistics-manager/me", {}, { silent: true });
        const me = res?.data?.data?.manager ?? res?.data?.manager ?? res?.data?.data ?? res?.data;
        if (!cancelled && me?.fullName) setInvitedBy(me.fullName);
      } catch {
        if (!cancelled) setInvitedBy("Logistics Manager");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const runEmailAvailabilityCheck = useCallback(async (rawEmail) => {
    const email = String(rawEmail || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailHint("");
      return;
    }
    const seq = ++emailCheckSeq.current;
    setEmailChecking(true);
    try {
      const result = await checkInviteLogisticsProviderEmail(email);
      if (seq !== emailCheckSeq.current) return;
      if (result?.status === "logistics_provider_exists") {
        const message = result.message || LOGISTICS_PROVIDER_EMAIL_EXISTS;
        setErrors((current) => ({ ...current, email: message }));
        setEmailHint("");
        return;
      }
      setErrors((current) => {
        if (current.email === LOGISTICS_PROVIDER_EMAIL_EXISTS) {
          const { email: _removed, ...rest } = current;
          return rest;
        }
        return current;
      });
      if (result?.status === "existing_user" && result.message) {
        setEmailHint(result.message);
      } else {
        setEmailHint("");
      }
    } catch (err) {
      if (seq !== emailCheckSeq.current) return;
      setEmailHint("");
    } finally {
      if (seq === emailCheckSeq.current) setEmailChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const email = form.email.trim();
    if (!email) {
      setEmailHint("");
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void runEmailAvailabilityCheck(email);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [form.email, open, runEmailAvailabilityCheck]);

  const invitationSummary = useMemo(() => {
    const now = new Date();
    return {
      loginEmail: form.email.trim() || "—",
      invitedBy: invitedBy || "—",
      invitedAt: now.toLocaleString(),
      status: "Pending Activation",
    };
  }, [form.email, invitedBy]);

  if (!open) return null;

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
    if (name === "email") setEmailHint("");
  };

  const validate = async () => {
    const next = {};
    if (!form.companyName.trim()) next.companyName = "Company name is required.";
    if (!form.contactPersonName.trim()) next.contactPersonName = "Contact person name is required.";
    const emailTrimmed = form.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      next.email = "Enter a valid email address.";
    } else {
      try {
        const result = await checkInviteLogisticsProviderEmail(emailTrimmed);
        if (result?.status === "logistics_provider_exists") {
          next.email = result.message || LOGISTICS_PROVIDER_EMAIL_EXISTS;
        } else if (result?.status === "existing_user" && result.message) {
          setEmailHint(result.message);
        }
      } catch (err) {
        next.email = extractApiMessage(err, "Could not validate email.");
      }
    }
    if (!form.mobile.trim()) next.mobile = "Mobile number is required.";
    if (!form.addressLine1.trim()) next.addressLine1 = "Address line 1 is required.";
    if (!form.countryId) next.countryId = "Country is required.";
    if (!form.stateId) next.stateId = "State is required.";
    if (!form.cityId) next.cityId = "City is required.";
    if (!form.pincode.trim()) next.pincode = "Postal code is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!(await validate())) return;
    setSaving(true);
    try {
      const payload = {
        companyName: form.companyName.trim(),
        contactPersonName: form.contactPersonName.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        alternateMobile: form.alternateMobile.trim() || null,
        status: form.status,
        addressLine1: form.addressLine1.trim(),
        addressLine2: form.addressLine2.trim() || null,
        city: labelForId(cities, form.cityId),
        state: labelForId(states, form.stateId),
        country: labelForId(countries, form.countryId),
        postalCode: form.pincode.trim(),
      };
      const data = await createAndInviteLogisticsProvider(payload);
      const defaultMessage =
        data?.scenario === "linked_existing_user"
          ? "Logistics Provider profile linked to the existing account. An activation email has been sent."
          : "Logistics provider created successfully. An invitation email has been sent to activate the account.";
      const message = data?.message || defaultMessage;
      if (data?.invitationSent === false) {
        toast.warning(
          `${message} The activation email could not be sent; please resend the invitation.`,
        );
      } else {
        toast.success(message);
      }
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      const message = extractApiMessage(err, "Could not create logistics provider.");
      toast.error(message);
      if (/email/i.test(message)) setErrors((e) => ({ ...e, email: message }));
      if (/mobile|phone/i.test(message)) setErrors((e) => ({ ...e, mobile: message }));
    } finally {
      setSaving(false);
    }
  };

  const modal = (
    <div
      className="sa-access-modal"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
    >
      <div
        className="sa-access-modal__panel sa-dispute-assign-modal sa-invite-lp-create-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-lp-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sa-dispute-assign-modal__head">
          <div className="sa-dispute-assign-modal__title-wrap">
            <span
              className="sa-dispute-assign-modal__icon sa-dispute-assign-modal__icon--assign"
              aria-hidden
            >
              <LuUserPlus />
            </span>
            <div className="sa-dispute-modal__head-main">
              <h2 id="create-lp-title">Create Logistics Provider</h2>
              <p className="sa-dispute-assign-modal__lead">
                Register a provider and send an email invitation to activate their account.
              </p>
            </div>
          </div>
          <button type="button" className="sa-dispute-icon-btn" aria-label="Close" onClick={onClose}>
            <LuX aria-hidden />
          </button>
        </header>

        <form className="sa-invite-lp-create-modal__form" onSubmit={onSubmit} noValidate>
          <div className="sa-dispute-assign-modal__body sa-invite-lp-create-modal__body">
            <section className="sa-access-section sa-user-form__section">
              <SectionHeading
                number="1"
                title="Basic Information"
                description="Company and primary contact details."
                icon={LuUserRound}
              />
              <div className="sa-access-grid sa-access-grid--four">
                <label className="sa-access-field">
                  <span>
                    Company Name <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    name="companyName"
                    value={form.companyName}
                    onChange={onChange}
                    placeholder="Enter company name"
                    aria-invalid={Boolean(errors.companyName)}
                  />
                  {errors.companyName ? <small className="sa-invite-lp-field-error">{errors.companyName}</small> : null}
                </label>
                <label className="sa-access-field">
                  <span>
                    Contact Person Name <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    name="contactPersonName"
                    value={form.contactPersonName}
                    onChange={onChange}
                    placeholder="Enter contact name"
                    aria-invalid={Boolean(errors.contactPersonName)}
                  />
                  {errors.contactPersonName ? (
                    <small className="sa-invite-lp-field-error">{errors.contactPersonName}</small>
                  ) : null}
                </label>
                <label className="sa-access-field">
                  <span>
                    Email Address <em>*</em>
                  </span>
                  <input
                    type="email"
                    className="input-pill"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    onBlur={() => void runEmailAvailabilityCheck(form.email)}
                    placeholder="name@company.com"
                    aria-invalid={Boolean(errors.email)}
                  />
                  {errors.email ? <small className="sa-invite-lp-field-error">{errors.email}</small> : null}
                  {!errors.email && emailHint ? (
                    <small className="sa-invite-lp-field-hint">{emailHint}</small>
                  ) : null}
                  {!errors.email && emailChecking ? (
                    <small className="sa-invite-lp-field-hint">Checking email…</small>
                  ) : null}
                </label>
                <label className="sa-access-field">
                  <span>
                    Mobile Number <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    name="mobile"
                    value={form.mobile}
                    onChange={onChange}
                    placeholder="Enter mobile number"
                    aria-invalid={Boolean(errors.mobile)}
                  />
                  {errors.mobile ? <small className="sa-invite-lp-field-error">{errors.mobile}</small> : null}
                </label>
                <label className="sa-access-field">
                  <span>Alternate Mobile Number</span>
                  <input
                    className="input-pill"
                    name="alternateMobile"
                    value={form.alternateMobile}
                    onChange={onChange}
                    placeholder="Optional"
                  />
                </label>
                <label className="sa-access-field">
                  <span>Status</span>
                  <select className="input-pill" name="status" value={form.status} onChange={onChange}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </label>
              </div>
            </section>

            <section className="sa-access-section sa-user-form__section">
              <SectionHeading
                number="2"
                title="Address Information"
                description="Registered business address."
                icon={LuMapPin}
              />
              <div className="sa-access-grid sa-access-grid--two" style={{ marginBottom: 14 }}>
                <label className="sa-access-field">
                  <span>
                    Address Line 1 <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    name="addressLine1"
                    value={form.addressLine1}
                    onChange={onChange}
                    placeholder="Street, building, suite"
                    aria-invalid={Boolean(errors.addressLine1)}
                  />
                  {errors.addressLine1 ? (
                    <small className="sa-invite-lp-field-error">{errors.addressLine1}</small>
                  ) : null}
                </label>
                <label className="sa-access-field">
                  <span>Address Line 2</span>
                  <input
                    className="input-pill"
                    name="addressLine2"
                    value={form.addressLine2}
                    onChange={onChange}
                    placeholder="Optional"
                  />
                </label>
              </div>
              <div className="sa-access-grid sa-access-grid--four">
                <label className="sa-access-field">
                  <span>
                    Country <em>*</em>
                  </span>
                  <select
                    className="input-pill"
                    value={form.countryId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setForm((c) => ({ ...c, countryId: id, stateId: "", cityId: "" }));
                      handleCountryChange(id);
                    }}
                    disabled={loadingCountries}
                    aria-invalid={Boolean(errors.countryId)}
                  >
                    <option value="">Select country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {errors.countryId ? <small className="sa-invite-lp-field-error">{errors.countryId}</small> : null}
                </label>
                <label className="sa-access-field">
                  <span>
                    State <em>*</em>
                  </span>
                  <select
                    className="input-pill"
                    value={form.stateId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setForm((c) => ({ ...c, stateId: id, cityId: "" }));
                      handleStateChange(form.countryId, id);
                    }}
                    disabled={loadingStates || !form.countryId}
                    aria-invalid={Boolean(errors.stateId)}
                  >
                    <option value="">Select state</option>
                    {states.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  {errors.stateId ? <small className="sa-invite-lp-field-error">{errors.stateId}</small> : null}
                </label>
                <label className="sa-access-field">
                  <span>
                    City <em>*</em>
                  </span>
                  <select
                    className="input-pill"
                    value={form.cityId}
                    onChange={(e) => setForm((c) => ({ ...c, cityId: e.target.value }))}
                    disabled={loadingCities || !form.stateId}
                    aria-invalid={Boolean(errors.cityId)}
                  >
                    <option value="">Select city</option>
                    {cities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {errors.cityId ? <small className="sa-invite-lp-field-error">{errors.cityId}</small> : null}
                </label>
                <label className="sa-access-field">
                  <span>
                    Postal Code <em>*</em>
                  </span>
                  <input
                    className="input-pill"
                    name="pincode"
                    value={form.pincode}
                    onChange={onChange}
                    placeholder="PIN / ZIP"
                    aria-invalid={Boolean(errors.pincode)}
                  />
                  {errors.pincode ? <small className="sa-invite-lp-field-error">{errors.pincode}</small> : null}
                </label>
              </div>
            </section>

            <section className="sa-access-section sa-user-form__section">
              <SectionHeading
                number="3"
                title="Invitation Details"
                description="Summary included in the activation email."
                icon={LuMail}
              />
              <div className="sa-invite-lp-create-modal__summary">
                <div className="sa-invite-lp-create-modal__summary-item">
                  <span className="sa-invite-lp-create-modal__summary-label">Login email</span>
                  <span className="sa-invite-lp-create-modal__summary-value">{invitationSummary.loginEmail}</span>
                </div>
                <div className="sa-invite-lp-create-modal__summary-item">
                  <span className="sa-invite-lp-create-modal__summary-label">Invited by</span>
                  <span className="sa-invite-lp-create-modal__summary-value">{invitationSummary.invitedBy}</span>
                </div>
                <div className="sa-invite-lp-create-modal__summary-item">
                  <span className="sa-invite-lp-create-modal__summary-label">Invitation date</span>
                  <span className="sa-invite-lp-create-modal__summary-value">{invitationSummary.invitedAt}</span>
                </div>
                <div className="sa-invite-lp-create-modal__summary-item">
                  <span className="sa-invite-lp-create-modal__summary-label">Status</span>
                  <span className="sa-invite-lp-create__badge">{invitationSummary.status}</span>
                </div>
              </div>
            </section>
          </div>

          <footer className="sa-dispute-assign-modal__footer">
            <button type="button" className="sa-access-btn sa-access-btn--ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="sa-access-btn sa-access-btn--primary" disabled={saving}>
              <LuSend aria-hidden /> {saving ? "Creating…" : "Create & Send Invitation"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
